import axios from 'axios'
import moment from 'moment-timezone'
// import store from '@/store'
import router from '@/router'
import md5 from 'js-md5'
import Vue from 'vue'
import { getToken } from '@/utils/auth'
import { Message } from 'element-ui'

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  withCredentials: false, // send cookies when cross-domain requests
  timeout: 300000, // request timeout
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// request interceptor
service.interceptors.request.use(
  (config) => {
    // do something before request is sent
    const time = moment
      .tz(moment(), 'Asia/Hong_Kong')
      .format('YYYY-MM-DD HH:mm:ss')
    // const secret = md5(`106734cf-ed78-423c-8e42-f35e3dac2869:CUHK-API:${time}`)
    // config.headers['secret'] = secret
    config.headers['request-time'] = time
    if (getToken()) {
      config.headers.Authorization = `Bearer ${getToken()}`
    }
    return config
  },
  (error) =>
    // do something with request error
    Promise.reject(error)
)

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code.
   */
  (response) => {
    const res = response

    // 呼叫方可帶 skipSuccessMessage: true 要求「不要自動彈操作成功」，
    // 交由頁面自己展示後端 notes[]（pos-id 治理模組的寫入類接口）。
    const skipSuccessMessage = Boolean(
      res.config && res.config.skipSuccessMessage
    )
    if (
      (res.status === 200 || res.status === 201) &&
      !skipSuccessMessage &&
      (res.config.method === 'put' ||
        res.config.method === 'post' ||
        res.config.method === 'delete')
    ) {
      if (
        res.config.url !== '/api/records/eng-by-date' &&
        res.config.url !== '/api/records/uncertain-list' &&
        res.config.url !== '/api/records/compare-night-and-final' &&
        res.config.url !== '/api/records/get-record-by-year' &&
        res.config.url !== '/api/login' &&
        res.config.url !== '/api/search-pos-id' &&
        res.config.url !== '/api/check-pos-id' &&
        res.config.url !== '/api/records/eng-by-date-export' &&
        res.config.url !== '/api/theatre-eng-select' &&
        res.config.url !== '/api/theatre-eng-manual-hint' &&
        res.config.url !== '/api/special-records/day-box-office'
      ) {
        Message.success('操作成功！')
      } else if (res.config.url === '/api/login') {
        Message.success('登錄成功！')
      }
    }
    // if the custom code is not 20000, it is judged as an error.
    if (res.status > 399) {
      Message.error(res.data.error.message || 'error')
      // Notify({ type: 'danger', message: res.data.error.message || 'error' })
      return Promise.reject(res.statusText || 'error')
    }
    // 呼叫方可帶 returnRawResponse: true 取得完整 axios response
    // （CSV 匯出需要 headers 的 Content-Disposition 才能取後端檔名）。
    if (res.config && res.config.returnRawResponse === true) {
      return res
    }
    if (res.status === 200 || res.status === 201) {
      return res.data
    }
  },
  (error) => {
    // 统一从错误响应中提取要展示的文案
    const extractMessage = (resp) => {
      if (resp && resp.data) {
        const { data } = resp
        // 1) 字段级校验失败（Laravel 422）：把各字段 errors 展平后拼接
        if (data.errors && typeof data.errors === 'object') {
          const list = Object.values(data.errors)
            .flat()
            .filter(Boolean)
          if (list.length) {
            return list.join('<br/>')
          }
        }
        // 2) 优先展示后端 message（401/403/500 等，后端可返回中文）
        if (data.message) {
          return data.message
        }
        // 3) 兼容旧格式：error.message
        if (data.error && data.error.message) {
          return data.error.message
        }
      }
      // 4) 状态码兜底文案
      return (
        {
          400: '請求錯誤',
          401: '登錄已過期，請重新登入',
          403: '沒有操作權限',
          404: '資源不存在或已刪除',
          409: '數據衝突，請稍後重試',
          422: '提交的數據有誤，請檢查後再試',
          500: '服務器錯誤，請稍後重試'
        }[resp && resp.status] || '請求失敗，請稍後重試'
      )
    }

    if (axios.isCancel(error)) {
      // 为了终结promise链 就是实际请求 不会走到.catch(rej=>{});这样就不会触发错误提示之类了。
      return new Promise(() => {})
    }

    // 呼叫方可帶 skipErrorMessage: true 要求「不要自動彈提示」，自行處理錯誤。
    // 使用場景：字典寫入的業務 422 需要彈自訂對話框（見 adminEditOptions.vue），
    // 否則會先彈一句後端英文訊息、再彈我們自己的中文框，變成雙份提示。
    const skipErrorMessage = Boolean(
      error.config && error.config.skipErrorMessage
    )

    let needsHTML = false
    let message = ''

    if (error.message && error.message.includes('timeout')) {
      message = 'Network connection error, please try again'
    } else if (!error.response) {
      message = 'Network connection failure'
    } else {
      const response = error.response
      // blob 下载出错：response.data 是一段需解析成 JSON 的文本
      if (error.config && error.config.responseType === 'blob') {
        const reader = new FileReader()
        const show = (text) => {
          if (!skipErrorMessage) Message.error(text || extractMessage(response))
        }
        reader.readAsText(response.data)
        reader.onload = function (e) {
          try {
            const jsonData = JSON.parse(e.target.result)
            if (!skipErrorMessage) {
              Message.error(jsonData.message || extractMessage(response))
            }
          } catch (parseErr) {
            if (!skipErrorMessage) Message.error('下載失敗，請稍後重試')
          }
        }
        reader.onerror = function () {
          show('')
        }
        // 非 blob：正常 JSON 错误体
      } else {
        message = extractMessage(response)
        // 含多行错误（errors 拼接）时需渲染 <br/>
        needsHTML = message.indexOf('<br/>') > -1
      }
    }

    if (message && !skipErrorMessage) {
      if (needsHTML) {
        Message({
          dangerouslyUseHTMLString: true,
          type: 'error',
          message
        })
      } else {
        Message.error(message)
      }
    }

    // 401：统一跳回登录页
    if (error.response && error.response.status === 401) {
      router.push('/login')
    }
    return Promise.reject(error)
  }
)

export default service
